import React, { useState } from 'react';
import { MdConfirmationNumber } from 'react-icons/md';
import * as Yup from 'yup';
import { Formik } from 'formik';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { TierDto } from 'legacy/events/types';
import { UserMe } from 'legacy/user/types';
import AlertModal from 'legacy/common/components/modal/AlertModal';
import { ButtonType } from 'legacy/common/components/button/Button';

import MemberOnlyAccess from './components/MemberOnlyAccess';
import MainForm from './components/MainForm';
import { GoToStepFn, StepsId } from './types';
import computeInitialValues from './helpers/computeInitialValues';
import { useRouter } from 'next/router';

type TierFormProps = {
  isTierPrivate: boolean;
  closeModal: () => void;
  ticket?: TierDto;
  onRemove?: () => void;
  user: UserMe;
  onSubmit: (values: TierDto) => void;
};

const modalTexts = {
  publicTier: {
    newTitle: 'New public tier',
    editTitle: 'Edit public tier'
  },
  privateTier: {
    newTitle: 'New private tier',
    editTitle: 'Edit private tier'
  }
};

const STEPS = {
  [StepsId.Main]: {
    icon: <MdConfirmationNumber size={20} />,
    onBack: null,
    title: (texts, isEditing) => (isEditing ? texts.editTitle : texts.newTitle),
    Component: MainForm,
    autoHeight: true
  },
  [StepsId.MemberOnlyAccess]: {
    icon: null,
    onBack: (goToStep) => () => goToStep(StepsId.Main),
    title: () => 'Member-Only Access',
    Component: MemberOnlyAccess,
    autoHeight: false
  }
};

const TicketForm = ({
  closeModal,
  ticket,
  isTierPrivate,
  onSubmit,
  ...props
}: TierFormProps) => {
  const isEditing = !!ticket;
  const texts = modalTexts[isTierPrivate ? 'privateTier' : 'publicTier'];
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(StepsId.Main);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
  const [pendingUrl, setPendingUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    const confirmationMessage = 'Changes you made may not be saved.';
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return confirmationMessage;
    };
    const beforeRouteHandler = (url: string) => {
      if (router.pathname !== url) {
        setPendingUrl(url);
        setShowUnsavedChangesAlert(true);
        router.events.emit('routeChangeError');
      }
    };
    if (isDirty) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
      router.events.on('routeChangeStart', beforeRouteHandler);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      router.events.off('routeChangeStart', beforeRouteHandler);
    }
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      router.events.off('routeChangeStart', beforeRouteHandler);
    };
  }, [isDirty]);

  const formData = STEPS[currentStep];

  const TicketSchema = Yup.object().shape({
    name: Yup.string().max(100).required('Required field').label('Name'),
    description: Yup.string().max(300).label('Description'),
    price: Yup.number()
      .min(0)
      .max(999999)
      .required('Required field')
      .label('Price'),
    maxAttendance: Yup.number()
      .min(
        ticket?.totalGuests || 0,
        ticket?.totalGuests
          ? 'You cannot decrease the number of available tickets to less than the number of tickets already sold.'
          : undefined
      )
      .max(999999)
      .label('Available Tickets'),
    private: Yup.boolean(),
    startDate: Yup.date(),
    endDate: Yup.date(),
    requiresApproval: Yup.boolean(),
    requiredOrganizationIds: Yup.array(Yup.string()).required(),
    requiredAssociationIds: Yup.array(Yup.string()).required()
  });

  const onFormSubmit = (values) => {
    const newValues: any = { ...values };
    delete newValues?.startDateTime;
    delete newValues?.endDateTime;
    const questions =
      newValues?.questions?.filter(({ question }) => question !== '') || [];

    newValues.questions = questions;
    onSubmit(newValues as TierDto);
    closeModal();
  };

  const goToStep: GoToStepFn = (stepId) => {
    setCurrentStep(stepId);
  };

  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedChangesAlert(true);
    } else {
      closeModal();
    }
  };

  return (
    <>
      <Modal
        visible
        onClose={handleClose}
        onBack={formData?.onBack?.(goToStep)}
        type={ModalType.SMALL}
        title={formData?.title(texts, isEditing)}
        icon={formData?.icon}
        autoHeight={formData?.autoHeight}
      >
        <Formik
          initialValues={computeInitialValues(ticket, isTierPrivate)}
          onSubmit={onFormSubmit}
          validationSchema={TicketSchema}
        >
          {({ submitForm }) => (
            <formData.Component
              goToStep={goToStep}
              ticket={ticket}
              isEditing={isEditing}
              isTierPrivate={isTierPrivate}
              submitForm={submitForm}
              setIsDirty={setIsDirty}
              /* eslint-disable react/jsx-props-no-spreading  */
              {...props}
            />
          )}
        </Formik>
      </Modal>
      {showUnsavedChangesAlert && (
        <AlertModal
          title="Unsaved Changes"
          message="You have unsaved changes that will be lost."
          submitLabel="Discard"
          submitButtonType={ButtonType.Danger}
          onSubmit={() => {
            setShowUnsavedChangesAlert(false);
            if (pendingUrl) {
              router.push(pendingUrl);
              setPendingUrl(null);
            }
            closeModal();
          }}
          onCancel={() => setShowUnsavedChangesAlert(false)}
        />
      )}
    </>
  );
};

export default TicketForm;
